import React, { useState, useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Card,
  Typography,
  Spin,
  message,
  Row,
  Col,
  Statistic,Alert,
  Divider,
  Descriptions
} from "antd";
import { DollarOutlined, UserOutlined } from "@ant-design/icons";
import axios from "axios";
import { getUserData } from "../../../hooks/Auth";

const { Title, Text } = Typography;
const { Option } = Select;
const { Item } = Form;
const user = getUserData();

const API_BASE_URL = "https://techcrew.pk/ulc/";

const SupplierPayment= () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [balanceData, setBalanceData] = useState(null);

  const fetchSuppliers = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}Get_Users.php`);
      if (data.status.code === 200) {
        const supplierList = data.data.filter(user => user.role === 'supplier'|| user.role === 'admin');
        setSuppliers(supplierList);
      }
    } catch (error) {
      console.error("Failed to fetch suppliers", error);
    }
  };

  const fetchSupplierBalance = async (supplierId) => {
    if (!supplierId) return;
    
    try {
      const { data } = await axios.get(`${API_BASE_URL}Get_CommissionLedger.php`, {
        params: { user_id: supplierId }
      });
      
      if (data.status.code === 200) {
        const received = data.data.transactions
          .filter(t => t.transaction_type === 'receive')
          .reduce((sum, t) => sum + parseFloat(t.amount), 0);
          
        const paid = data.data.transactions
          .filter(t => t.transaction_type === 'payment')
          .reduce((sum, t) => sum + parseFloat(t.amount), 0);
          
        setBalanceData({
          received,
          paid,
          balance: received - paid
        });
      }
    } catch (error) {
      console.error("Failed to fetch balance", error);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const payload = {
        supplier_id: values.supplier_id,
        amount: values.amount,
        notes: values.notes || `Payment to supplier`,
        created_by: user?.user?.id || 0
      };

      const { data } = await axios.post(`${API_BASE_URL}Send_PaymentToSupplier.php`, payload);
      
      if (data.status.code === 200) {
        message.success(`Payment of ${values.amount} SAR sent successfully`);
        form.resetFields();
        fetchSupplierBalance(values.supplier_id);
      } else {
        throw new Error(data.status.message);
      }
    } catch (error) {
      message.error(error.response?.data?.status?.message || error.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  const handleSupplierChange = (supplierId) => {
    fetchSupplierBalance(supplierId);
  };

  useEffect(() => {
    fetchSuppliers();
  }, []);

  return (
    <div style={{ paddingLeft: "250px", paddingTop: "60px" }}>
      <Card
        title={<Title level={4} style={{ margin: 0 }}>Supplier Payment</Title>}
        bordered={false}
        style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSubmit}
              initialValues={{ notes: 'Payment to supplier' }}
            >
              <Item
                name="supplier_id"
                label="Supplier"
                rules={[{ required: true, message: 'Please select supplier' }]}
              >
                <Select
                  placeholder="Select supplier"
                  onChange={handleSupplierChange}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {suppliers.map(supplier => (
                    <Option key={supplier.user_id} value={supplier.user_id}>
                      {supplier.name} ({supplier.email})
                    </Option>
                  ))}
                </Select>
              </Item>
              
              <Item
                name="amount"
                label="Amount (SAR)"
                rules={[
                  { required: true, message: 'Please enter amount' },
                  { pattern: /^[0-9]+(\.[0-9]{1,2})?$/, message: 'Invalid amount' }
                ]}
              >
                <Input prefix={<DollarOutlined />} />
              </Item>
              
              <Item name="notes" label="Notes">
                <Input.TextArea rows={3} />
              </Item>
              
              <Item>
                <Button 
                  type="primary" 
                  htmlType="submit"
                  loading={loading}
                  disabled={!balanceData}
                >
                  Send Payment
                </Button>
              </Item>
            </Form>
          </Col>
          
          <Col span={12}>
            {balanceData && (
              <Card title="Supplier Balance Summary">
                <Row gutter={16}>
                  <Col span={12}>
                    <Statistic
                      title="Total Received"
                      value={balanceData.received}
                      precision={2}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: '#3f8600' }}
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Total Paid"
                      value={balanceData.paid}
                      precision={2}
                      prefix={<DollarOutlined />}
                      valueStyle={{ color: '#cf1322' }}
                    />
                  </Col>
                </Row>
                <Divider />
                <Statistic
                  title="Current Balance"
                  value={balanceData.balance}
                  precision={2}
                  prefix={<DollarOutlined />}
                  valueStyle={{
                    color: balanceData.balance > 0 ? '#3f8600' : 
                          balanceData.balance < 0 ? '#cf1322' : '#000'
                  }}
                />
                <Alert
                  message={
                    balanceData.balance > 0 ? 
                      'Supplier has pending balance' :
                    balanceData.balance < 0 ?
                      'Supplier has been overpaid' :
                      'Account is balanced'
                  }
                  type={
                    balanceData.balance > 0 ? 'success' :
                    balanceData.balance < 0 ? 'error' : 'info'
                  }
                  showIcon
                  style={{ marginTop: 16 }}
                />
              </Card>
            )}
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default SupplierPayment;